import { useState } from "react";
import {
  LuCircleHelp,
  LuLoaderCircle,
  LuPencil,
  LuTrash2,
  LuTriangleAlert,
} from "react-icons/lu";
import { colors, effects, typography } from "../../../design/tokens";
import { HttpAssistantIsWorkingException } from "../../../model/httpException";
import { useSnackbar } from "../../../providers/SnackbarProvider";
import { ImageService } from "../../../services/imageService";
import {
  useHasMultipleUserMessages,
  useLastUserMessage,
} from "../../../view_stores/chat_messages/useChatMessagesStore";
import {
  usePromptErrorDetails,
  usePromptErrorHandlerActions,
} from "../../../view_stores/usePromptErrorHandlerStore";
import { usePromptInputActions } from "../../../view_stores/usePromptInputStore";

// Helper function to convert attachment to File
async function attachmentToFile(attachment: {
  url?: string;
  content?: string;
  filename: string;
  mimeType: string;
}): Promise<File | null> {
  // Check if attachment has URL (new format) or content (legacy format)
  if (attachment.url) {
    // Fetch image from URL and convert to File
    return await ImageService.fetchImageAsFile(
      attachment.url,
      attachment.filename,
      attachment.mimeType,
    );
  } else if (attachment.content) {
    // Use legacy base64 content
    return ImageService.base64ToFile(
      attachment.content,
      attachment.filename,
      attachment.mimeType,
    );
  }
  return null;
}

export default function ProjectPromptErrorActions({
  onEdit,
}: {
  onEdit: () => void;
}) {
  const promptErrorDetails = usePromptErrorDetails();

  if (!promptErrorDetails) {
    return null;
  }

  return (
    <div
      className="h-full w-full overflow-hidden
    flex flex-col items-center justify-start"
    >
      <div
        className={`border ${effects.borderRadius.xl} border-dark-border p-6 max-w-[600px] w-full`}
      >
        <div className="flex flex-col items-center gap-6">
          {/* Error message section */}
          <div className="w-full flex items-start gap-3">
            <LuTriangleAlert
              className="text-red-500 flex-shrink-0 mt-0.5"
              size={24}
            />
            <div className="flex-1">
              <p
                className={`${typography.size.base} ${colors.text.primary} leading-[1.6]`}
              >
                {promptErrorDetails.reason === "prompt_too_long" ? (
                  <>
                    Your message is too long. Please shorten your message or
                    remove some file references to continue.
                  </>
                ) : (
                  promptErrorDetails.message
                )}
              </p>
            </div>
          </div>

          {/* Action prompt section */}
          <div className="w-full flex items-center justify-between">
            <div
              className={`${typography.size.base} ${colors.text.primary} leading-[1.6]`}
            >
              How would you like to proceed?
            </div>
            <HelpTooltip errorDetails={promptErrorDetails} />
          </div>

          <Actions onEdit={onEdit} />
        </div>
      </div>
    </div>
  );
}

function HelpTooltip({
  errorDetails,
}: {
  errorDetails: { message: string; reason: string };
}) {
  const hasMultipleUserMessages = useHasMultipleUserMessages();
  const canDecline = hasMultipleUserMessages;

  const tooltipContent = () => {
    let content = `${errorDetails.message}<br/><br/>Please choose how you'd like to proceed:
          <ul style="list-style-type: disc; padding-left: 12px;">
            <li>Edit: Modify your request and try again.</li>`;

    if (canDecline) {
      content += `<li>Discard: Cancel the last change and revert to the previous version of the project.</li>`;
    }
    content += `</ul>`;

    if (errorDetails.reason === "prompt_too_long") {
      content += `<br><i>Tip: Try shortening your message or removing some file references.</i>`;
    }

    return content;
  };

  return (
    <span data-tooltip-id="chat-tooltip" data-tooltip-html={tooltipContent()}>
      <LuCircleHelp
        className={`${colors.text.secondary} cursor-pointer ${colors.hover.opacity}`}
        size={20}
      />
    </span>
  );
}

function Actions({ onEdit }: { onEdit: () => void }) {
  const [selectedActionIndex, setSelectedActionIndex] = useState<number | null>(
    null,
  );
  const lastUserMessage = useLastUserMessage();

  const { rollback } = usePromptErrorHandlerActions();
  const { setPrompt, setAttachedImage } = usePromptInputActions();
  const { showSnackbar } = useSnackbar();

  const handleEdit = async () => {
    setSelectedActionIndex(1);
    onEdit();

    if (lastUserMessage) {
      setPrompt(lastUserMessage.value.message);
      if (
        lastUserMessage.value.attachments &&
        lastUserMessage.value.attachments.length > 0
      ) {
        const file = await attachmentToFile(
          lastUserMessage.value.attachments[0],
        );
        setAttachedImage(file);
      }
    }
  };

  const handleDiscard = async () => {
    setSelectedActionIndex(2);
    try {
      await rollback(null, null);
    } catch (error) {
      setSelectedActionIndex(null);
      if (error instanceof HttpAssistantIsWorkingException) {
        showSnackbar("Previous action is still in progress, try again later.", {
          type: "error",
          duration: 5000,
        });
      } else {
        throw error;
      }
    }
  };

  return (
    <div className="w-full flex gap-4">
      <ActionBtn
        id="project-prompt-error-edit"
        label="Edit"
        icon={<LuPencil />}
        isDisabled={selectedActionIndex !== null}
        isLoading={selectedActionIndex === 0}
        bgColor="bg-[#16A34A]"
        textColor="text-white"
        onClick={handleEdit}
      />

      <ActionBtn
        id="project-prompt-error-discard"
        label="Discard"
        icon={<LuTrash2 />}
        isDisabled={selectedActionIndex !== null}
        isLoading={selectedActionIndex === 1}
        bgColor="bg-dark-surface-secondary"
        textColor={colors.text.secondary}
        onClick={handleDiscard}
      />
    </div>
  );
}

function ActionBtn({
  id,
  label,
  icon,
  isDisabled,
  isLoading,
  bgColor,
  textColor,
  onClick,
}: {
  id: string;
  label: string;
  icon: React.ReactNode;
  isDisabled: boolean;
  isLoading: boolean;
  bgColor: string;
  textColor: string;
  onClick: () => void;
}) {
  return (
    <div
      className={`w-full ${effects.borderRadius.md}
    relative overflow-hidden`}
    >
      <button
        id={id}
        onClick={onClick}
        className={`w-full flex items-center justify-center
    gap-2 py-2
    ${colors.hover.brightness} hover:cursor-pointer
    ${effects.transition.default} ${typography.size.sm}
    ${bgColor} ${textColor}`}
        disabled={isDisabled}
      >
        {icon}
        {label}
      </button>
      {isLoading && (
        <div
          className="absolute top-0 bg-black/50 w-full h-full
          flex items-center justify-center"
        >
          <LuLoaderCircle className="animate-spin text-white" size={24} />
        </div>
      )}
    </div>
  );
}
